import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { APIError } from "@/api/client";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuthStore } from "@/stores/authStore";

// Bietet Chrome/Google das Speichern der Anmeldedaten aktiv an. Nötig, weil der
// Login per fetch läuft (SPA, kein klassischer Formular-Versand) und der Browser
// das „Passwort speichern?" sonst nicht von selbst auslöst. No-op außerhalb von
// Chromium oder ohne HTTPS.
async function rememberCredential(id: string, password: string): Promise<void> {
  try {
    const w = window as unknown as {
      PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    };
    if (w.PasswordCredential && navigator.credentials?.store) {
      await navigator.credentials.store(new w.PasswordCredential({ id, password }));
    }
  } catch {
    /* Browser unterstützt es nicht oder die Nutzer:in lehnt ab — egal. */
  }
}

export default function Login() {
  const { t } = useTranslation();
  const { login, isLoading } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      await rememberCredential(email, password);
      // Echte Seiten-Navigation statt SPA-Routenwechsel: Chromes Passwort-
      // Manager löst „Passwort speichern?" nur bei Form-Submit + Navigation
      // aus. Tokens liegen in localStorage, der Auth-State überlebt den Reload.
      // BASE_URL = "/ca/" (vite base) — ohne Prefix verlässt "/" die App.
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      setError(err instanceof APIError ? err.detail : t("auth.loginFailed"));
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <h1 className="text-5xl font-serif font-semibold text-ca-brand tracking-tight mb-2">
        Nudging Bloom
      </h1>
      <p className="text-ca-ink-muted uppercase tracking-widest font-bold text-sm mb-12">
        {t("auth.tagline")}
      </p>

      <form
        onSubmit={submit}
        className="bg-ca-card rounded-3xl p-10 w-full max-w-md border border-ca-surface-border space-y-6"
      >
        <h2 className="text-2xl font-black text-ca-surface-ink uppercase tracking-tight">
          {t("auth.loginTitle")}
        </h2>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.email")}
          </label>
          <input
            type="email"
            name="email"
            id="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
            required
          />
        </div>
        <div>
          <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
            {t("auth.password")}
          </label>
          <input
            type="password"
            name="password"
            id="current-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
            required
          />
        </div>
        {error && <p className="text-verdict-red text-sm">{error}</p>}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl hover:scale-[1.02] disabled:opacity-50 transition-all active:scale-95"
        >
          {isLoading ? "..." : t("auth.loginButton")}
        </button>
        <p className="text-center text-sm text-ca-surface-muted">
          {t("auth.noAccount")}{" "}
          <Link
            to="/register"
            className="font-semibold text-ca-surface-accent underline decoration-ca-accent decoration-2 underline-offset-4 hover:text-brand-navy-light"
          >
            {t("auth.registerLink")}
          </Link>
        </p>
        <p className="text-center text-sm text-ca-surface-muted">
          <Link
            to="/forgot"
            className="font-semibold text-ca-surface-accent underline decoration-ca-accent decoration-2 underline-offset-4 hover:text-brand-navy-light"
          >
            {t("auth.forgotLink")}
          </Link>
        </p>
        <p className="text-center text-xs text-ca-surface-muted">
          <Link to="/datenschutz" className="hover:text-ca-surface-muted">
            {t("auth.privacyLink")}
          </Link>
        </p>
      </form>
    </div>
  );
}
